import { cn } from '../lib/utils';
import type { SortDir } from './ColumnMenu';

/**
 * One level of a stacked sort.
 *
 * Modelled on Excel's Sort dialog, which is what Andrea already uses: Column,
 * Sort On, Order. Two kinds of level because her sheet has two kinds of sort.
 *
 *  - 'value' sorts on the underlying value, oldest to newest or A to Z. This is
 *    Excel's "Sort on: Values".
 *  - 'status' pushes one status to the top or the bottom of the list. This is
 *    Excel's "Sort on: Cell Color". Our colours are status fields rather than
 *    cell formatting, so the colour has to be named as the status it is.
 *
 * Andrea, 29 Jul, describing what she does at close of day: orange first, then
 * yellow, then oldest to newest, then green at the bottom. Her colour mapping,
 * 12 Jun: pending is yellow, in progress is orange, complete is green. So her
 * end-of-day view is four levels on the Pour column:
 *
 *   1  Pour  Status  In progress on top
 *   2  Pour  Status  Pending on top
 *   3  Pour  Status  Complete on bottom
 *   4  Pour  Values  Oldest to newest
 *
 * Levels apply in order and the first one that separates two rows wins, exactly
 * as Excel does it. That ordering matters and it is hers to set: putting the
 * Values level above the Complete-on-bottom level scatters the green, because
 * the dates would already have separated every row before the colour level got
 * a chance to look. The panel shows the effect immediately so a wrong order is
 * visible rather than theoretical.
 */
export type SortLevel =
    | { kind: 'value'; colId: string; dir: SortDir }
    | { kind: 'status'; colId: string; value: string; position: 'top' | 'bottom' };

const selectBase =
    'bg-graphite-50 dark:bg-graphite-950 border border-graphite-300 dark:border-graphite-700 ' +
    'rounded px-2 py-1.5 font-mono text-[11px] text-graphite-900 dark:text-graphite-100 ' +
    'focus:border-signal-500 focus:outline-none min-w-0';

const iconBtn =
    'px-1.5 py-1 rounded text-graphite-500 hover:text-graphite-900 dark:hover:text-graphite-200 ' +
    'hover:bg-graphite-100 dark:hover:bg-graphite-800 disabled:opacity-30 ' +
    'disabled:hover:bg-transparent disabled:cursor-default transition-colors';

export function SortPanel({
    levels,
    columns,
    facetsFor,
    onChange,
    onClose,
}: {
    levels: SortLevel[];
    columns: { id: string; label: string }[];
    /** The discrete values present in a column right now, for the status levels. */
    facetsFor: (colId: string) => string[];
    onChange: (levels: SortLevel[]) => void;
    onClose: () => void;
}) {
    function replace(i: number, next: SortLevel) {
        onChange(levels.map((l, k) => (k === i ? next : l)));
    }

    function move(i: number, delta: number) {
        const j = i + delta;
        if (j < 0 || j >= levels.length) return;
        const next = [...levels];
        const tmp = next[i];
        next[i] = next[j];
        next[j] = tmp;
        onChange(next);
    }

    function addLevel() {
        const colId = columns[0]?.id ?? '';
        onChange([...levels, { kind: 'value', colId, dir: 'asc' }]);
    }

    // Changing the column on a status level can strand it on a value the new
    // column does not have, so the value is re-seeded from the new column.
    function setColumn(i: number, colId: string) {
        const l = levels[i];
        if (l.kind === 'value') return replace(i, { ...l, colId });
        const opts = facetsFor(colId);
        replace(i, { ...l, colId, value: opts.includes(l.value) ? l.value : (opts[0] ?? '') });
    }

    function setKind(i: number, kind: SortLevel['kind']) {
        const l = levels[i];
        if (kind === l.kind) return;
        if (kind === 'value') return replace(i, { kind: 'value', colId: l.colId, dir: 'asc' });
        const opts = facetsFor(l.colId);
        replace(i, { kind: 'status', colId: l.colId, value: opts[0] ?? '', position: 'top' });
    }

    return (
        <>
            <div className="fixed inset-0 z-40 bg-graphite-950/40" onClick={onClose} />
            <div
                role="dialog"
                aria-label="Sort levels"
                className={cn(
                    'fixed z-50 left-1/2 top-24 -translate-x-1/2 w-[min(680px,calc(100vw-2rem))]',
                    'bg-white dark:bg-graphite-900 border border-graphite-300 dark:border-graphite-700',
                    'rounded shadow-card-md overflow-hidden',
                )}
            >
                <div className="px-4 py-2.5 border-b border-graphite-200 dark:border-graphite-800 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400">
                        Sort
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-graphite-500 hover:text-graphite-900 dark:hover:text-graphite-200 -m-1 p-1"
                    >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                    </button>
                </div>

                <div className="px-4 py-3 max-h-[60vh] overflow-auto flex flex-col gap-2">
                    {levels.length === 0 ? (
                        <p className="text-xs text-graphite-500 py-2">
                            No sort yet. Add a level, or click any column heading for a quick sort.
                        </p>
                    ) : null}

                    {levels.map((l, i) => {
                        const facets = l.kind === 'status' ? facetsFor(l.colId) : [];
                        return (
                            <div key={i} className="flex items-center gap-2">
                                <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-500 w-14 shrink-0">
                                    {i === 0 ? 'Sort by' : 'Then by'}
                                </span>

                                <select
                                    aria-label={`Level ${i + 1} column`}
                                    className={cn(selectBase, 'flex-1')}
                                    value={l.colId}
                                    onChange={(e) => setColumn(i, e.target.value)}
                                >
                                    {columns.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    aria-label={`Level ${i + 1} sort on`}
                                    className={cn(selectBase, 'w-24 shrink-0')}
                                    value={l.kind}
                                    onChange={(e) => setKind(i, e.target.value as SortLevel['kind'])}
                                >
                                    <option value="value">Values</option>
                                    <option value="status">Status</option>
                                </select>

                                {l.kind === 'value' ? (
                                    <select
                                        aria-label={`Level ${i + 1} order`}
                                        className={cn(selectBase, 'w-40 shrink-0')}
                                        value={l.dir}
                                        onChange={(e) =>
                                            replace(i, { ...l, dir: e.target.value as SortDir })
                                        }
                                    >
                                        <option value="asc">Oldest / A to Z</option>
                                        <option value="desc">Newest / Z to A</option>
                                    </select>
                                ) : (
                                    <>
                                        <select
                                            aria-label={`Level ${i + 1} status`}
                                            className={cn(selectBase, 'w-40 shrink-0')}
                                            value={l.value}
                                            onChange={(e) => replace(i, { ...l, value: e.target.value })}
                                        >
                                            {facets.length === 0 ? (
                                                <option value="">No values</option>
                                            ) : null}
                                            {facets.map((f) => (
                                                <option key={f} value={f}>
                                                    {f}
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            aria-label={`Level ${i + 1} position`}
                                            className={cn(selectBase, 'w-28 shrink-0')}
                                            value={l.position}
                                            onChange={(e) =>
                                                replace(i, {
                                                    ...l,
                                                    position: e.target.value as 'top' | 'bottom',
                                                })
                                            }
                                        >
                                            <option value="top">On top</option>
                                            <option value="bottom">On bottom</option>
                                        </select>
                                    </>
                                )}

                                <div className="flex items-center shrink-0">
                                    <button
                                        type="button"
                                        className={iconBtn}
                                        disabled={i === 0}
                                        onClick={() => move(i, -1)}
                                        aria-label={`Move level ${i + 1} up`}
                                        title="Move up"
                                    >
                                        &uarr;
                                    </button>
                                    <button
                                        type="button"
                                        className={iconBtn}
                                        disabled={i === levels.length - 1}
                                        onClick={() => move(i, 1)}
                                        aria-label={`Move level ${i + 1} down`}
                                        title="Move down"
                                    >
                                        &darr;
                                    </button>
                                    <button
                                        type="button"
                                        className={iconBtn}
                                        onClick={() => onChange(levels.filter((_, k) => k !== i))}
                                        aria-label={`Remove level ${i + 1}`}
                                        title="Remove this level"
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="px-4 py-2.5 border-t border-graphite-200 dark:border-graphite-800 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={addLevel}
                        disabled={columns.length === 0}
                        className="px-2.5 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider text-signal-700 dark:text-signal-300 bg-signal-500/15 hover:bg-signal-500/25 disabled:opacity-40 transition-colors"
                    >
                        + Add level
                    </button>
                    {levels.length > 0 ? (
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            className="px-2.5 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider text-graphite-600 dark:text-graphite-400 hover:bg-graphite-100 dark:hover:bg-graphite-800 transition-colors"
                        >
                            Clear all
                        </button>
                    ) : null}
                    <div className="flex-1" />
                    <p className="font-mono text-[10px] text-graphite-400 dark:text-graphite-600">
                        Levels apply top to bottom
                    </p>
                </div>
            </div>
        </>
    );
}
